import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useLocation, useParams } from 'react-router'
import styled from 'styled-components'
import qs, { ParsedUrl } from 'query-string'

import * as UserActions from '../../actions/user'
import { Button, Centered, Column, Footer, Headings, Loader, Logo } from '../../components'
import Padding, { Sizes as PaddingSizes } from '../../components/Padding'
import { colors, routes } from '../../constants'
import * as AppSelectors from '../../reducers/app'
import * as UserSelectors from '../../reducers/user'
import { useTranslation } from 'react-i18next'
import { RootState } from '../../reducers'
import { usePageTitle } from '../../actions/app'

const ErrorMessage = styled.div`
    color: ${colors.red};
    padding: 15px;
    text-align: center;
    width: 100%;
`

const FooterText = styled.span`
    color: ${colors.greydark};
`

/**
 * Login Container
 */
export default function Login(): JSX.Element {
    usePageTitle()

    const dispatch = useDispatch()
    const { state } = useLocation<{ from: string }>()
    const { session } = useParams<{ session?: string }>()
    const error: string = useSelector<RootState, string>((state) => state.user?.session?.error)
    const isLogingWithSession: boolean = useSelector<RootState, boolean>((state) => state.user?.isLogingWithSession)
    const isAuthenticated: boolean = useSelector<RootState, boolean>(UserSelectors.isUserAuthenticated)
    const afterLoginPath: string = useSelector<RootState, string>(UserSelectors.getRedirectAfterLogin)
    const isMobile: boolean = useSelector(AppSelectors.selectIsMobile)
    const isScreenLarge: boolean = useSelector(AppSelectors.selectIsHeightLG)
    const isWorking: boolean = useSelector<RootState, boolean>((state) => state.user?.session?.isWorking)
    const { t } = useTranslation()

    React.useEffect((): void => {
        if (session) {
            // If a session id is passed, login using it
            dispatch(UserActions.loginWithSession(session))
        }
    }, [dispatch, session])

    React.useEffect((): void => {
        if (isAuthenticated || session) {
            return
        }

        const urlFrom: ParsedUrl = qs.parseUrl(state?.from || '')
        const authCode: string = (urlFrom.query.code || '') as string
        const authState: string = (urlFrom.query.state || '') as string

        if (!authCode || !authState) {
            return
        }

        dispatch(UserActions.loginV2Resume(authCode as string, authState as string))
    }, [dispatch, state?.from, isAuthenticated, session])

    if (isAuthenticated) {
        return <Redirect to={afterLoginPath || routes.ROOT} />
    }

    if (isLogingWithSession) {
        return (
            <Centered horizontal vertical>
                <Loader />
            </Centered>
        )
    }

    // const login = (): void => {
    //     dispatch(UserActions.redirectAfterLogin(state?.from))
    //     dispatch(UserActions.loginV2())
    // }

    const getCurrentYear = (): number => new Date().getFullYear()

    const renderForm = (): JSX.Element => (
        <Padding size={PaddingSizes.md}>
            <Headings.H1>
                {t('Welcome to')}
                &nbsp;
            </Headings.H1>
            <Headings.H1 bold color={colors.blue}>
                Hubble
            </Headings.H1>
            <Padding size={PaddingSizes.md} top>
                <Padding size={PaddingSizes.sm} top>
                    {isWorking && (
                        <Centered horizontal>
                            <Loader />
                        </Centered>
                    )}
                    {!isWorking && (
                        <div style={{ fontSize: '20px' }}>Hubble 2 has now been retired and all remaining content has been migrated to Hubble 3.<br />Please email us at <a href="mailto:contact@volumenetwork.com">contact@volumenetwork.com</a> to discuss new account logins.</div>
                    )}
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </Padding>
            </Padding>
        </Padding>
    )

    return (
        <>
            {!isMobile && (
                <Column withBackground={true} color={colors.blue}>
                    <Centered horizontal vertical>
                        <Logo />
                    </Centered>
                </Column>
            )}
            <Column width={isMobile ? '100%' : '50%'}>
                {isScreenLarge && <Centered vertical>{renderForm()}</Centered>}
                {!isScreenLarge && renderForm()}
                <Footer background={colors.bluelightest}>
                    <FooterText>{`© ${getCurrentYear()} Volume. All Rights Reserved.`}</FooterText>
                </Footer>
            </Column>
        </>
    )
}
